<script lang="ts" setup>
import { computed, onMounted, ref, shallowRef } from 'vue';
import Dialog from 'primevue/dialog';

import { AddAdmin, AddOrganization, AddTenant, Steps } from './steps';
import { useOrganizationStore, usePeopleStore, usePeopleTypesStore, useTenantStore } from '@/store';
import { EMIT } from '@/enum';
import { useToast } from 'primevue/usetoast';

const tenantStore = useTenantStore();
const peopleStore = usePeopleStore();
const organizationStore = useOrganizationStore();
const peopleTypeStore = usePeopleTypesStore();

const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE]);
const modalVisible = ref(true);
const toast = useToast();
const PEOPLE_TYPES = peopleTypeStore.PEOPLE_TYPES;

const items = [
  {
    id: 0,
    allowNavigate: true,
    component: AddTenant,
    complete: false,
  },
  {
    id: 1,
    allowNavigate: false,
    component: AddOrganization,
    complete: false,
  },
  {
    id: 2,
    allowNavigate: false,
    component: AddAdmin,
    complete: false,
  },
];

const step = shallowRef(items[0]);

onMounted(() => {
  init();
});

/**
 * get the people types so we can attach the admin type ID to the admin People object
 */
const init = () => {
  if (!peopleTypeStore.types.length) peopleTypeStore.getPeopleTypes();
};

const loading = computed(() => tenantStore.loading || organizationStore.loading || peopleStore.loading);
/**
 * on Next button press either go to the next for or submit the whole shebang
 */
const handleFormNext = () => {
  if (step.value.id < 2) {
    step.value.allowNavigate = true;
    step.value.complete = true;
    step.value = items[step.value.id + 1];
  }
};

/**
 * save the tenant, admin and org through their respective stores
 */
const handleFormSave = async () => {
  try {
    setPayload();
    await tenantStore.saveTenant();
    doToast(false, 'Tenant Saved');
    modalVisible.value = false;
    emit(EMIT.SAVE);
  } catch (e) {
    doToast(true, 'something went wrong. please try again ');
  }
};

/**
 * set the payload data for the Tenant, Org, and Admin
 */
const setPayload = () => {
  setAdminPersonType();
  tenantStore.newTenant.timezone_id = tenantStore.newAdmin.timezone_id;
  tenantStore.newTenant.email = tenantStore.newAdmin.email;
};

/**
 * set the proper person type based on PeopleTypes from the storeApi for the admin we are creating
 */
const setAdminPersonType = () => {
  const personType = peopleTypeStore.types.find((type) => type.acronym === PEOPLE_TYPES.SCHOOL_ADMIN);
  if (personType) tenantStore.newAdmin.person_type_id = personType.id;
};

/**
 * Display the toast message.
 */
const doToast = (error: boolean, msg?: string) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: msg,
    life: 3000,
  });
};

/**
 * on Back make sure the next form has allowed Navigation and then go there
 */
const handleFormBack = () => {
  const newStep = items[step.value.id - 1];
  if (newStep.allowNavigate && step.value.id > 0) step.value = newStep;
};

/**
 * Handle the cancellation of the user create modal.
 */
const onCancel = () => {
  modalVisible.value = false;
  emit(EMIT.CANCEL);
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    :modal="true"
    header="Create New Tenant"
    :draggable="false"
    :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
    :style="{ minWidth: '600px' }"
    class="wizard_dialog__container"
    @hide="onCancel"
  >
    <Loader v-if="loading" :relative="true" class="flex justify-content-center align-items-center h-full loader" />
    <Steps :id="step.id" />
    <AddTenant v-if="step.id === 0" @next="handleFormNext" />
    <AddOrganization v-if="step.id === 1" @next="handleFormNext" @back="handleFormBack" />
    <AddAdmin v-if="step.id === 2" @save="handleFormSave" @back="handleFormBack" />
  </Dialog>
</template>

<style lang="scss" scoped>
.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
